"use client";

import { c as _c } from "react/compiler-runtime";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cn } from "../../../../shared/utils/cn.js";
import { CreditCard, Wallet } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useState } from "react";
import { CheckoutOrderStatus } from "./CheckoutOrderStatus.js";
import { CoinbaseCheckoutPanel } from "./CoinbaseCheckoutPanel.js";
import { CryptoPayPanel } from "./CryptoPayPanel.js";
import { FiatCheckoutPanel } from "./FiatCheckoutPanel.js";
const SESSION_STORAGE_KEY = "anyspend_checkout_orderId";
function RadioCircle({
  selected,
  themeColor
}) {
  return _jsx("div", {
    className: cn("flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 transition-colors", selected ? "border-blue-600" : "border-gray-300 dark:border-neutral-600"),
    style: selected && themeColor ? {
      borderColor: themeColor
    } : undefined,
    children: selected && _jsx("div", {
      className: "h-2 w-2 rounded-full bg-blue-600",
      style: themeColor ? {
        backgroundColor: themeColor
      } : undefined
    })
  });
}
/** Card brand logos - all use consistent 32x20 viewBox */
function VisaLogo() {
  return _jsxs("svg", {
    viewBox: "0 0 32 20",
    style: {
      width: 32,
      height: 20
    },
    "aria-label": "Visa",
    children: [_jsx("rect", {
      width: "32",
      height: "20",
      rx: "3",
      fill: "#1A1F71"
    }), _jsx("text", {
      x: "16",
      y: "13.5",
      textAnchor: "middle",
      fill: "white",
      fontSize: "9",
      fontWeight: "bold",
      fontFamily: "sans-serif",
      fontStyle: "italic",
      children: "VISA"
    })]
  });
}
function MastercardLogo() {
  return _jsxs("svg", {
    viewBox: "0 0 32 20",
    style: {
      width: 32,
      height: 20
    },
    "aria-label": "Mastercard",
    children: [_jsx("rect", {
      width: "32",
      height: "20",
      rx: "3",
      fill: "#252525"
    }), _jsx("circle", {
      cx: "12.5",
      cy: "10",
      r: "6",
      fill: "#EB001B"
    }), _jsx("circle", {
      cx: "19.5",
      cy: "10",
      r: "6",
      fill: "#F79E1B"
    }), _jsx("path", {
      d: "M16 5.6a6 6 0 0 1 0 8.8 6 6 0 0 1 0-8.8z",
      fill: "#FF5F00"
    })]
  });
}
function AmexLogo() {
  return _jsxs("svg", {
    viewBox: "0 0 32 20",
    style: {
      width: 32,
      height: 20
    },
    "aria-label": "Amex",
    children: [_jsx("rect", {
      width: "32",
      height: "20",
      rx: "3",
      fill: "#006FCF"
    }), _jsx("text", {
      x: "16",
      y: "13",
      textAnchor: "middle",
      fill: "white",
      fontSize: "7",
      fontWeight: "bold",
      fontFamily: "sans-serif",
      children: "AMEX"
    })]
  });
}
/** Coinbase mark */
function CoinbaseLogo() {
  return _jsxs("svg", {
    viewBox: "0 0 24 24",
    style: {
      width: 20,
      height: 20
    },
    "aria-label": "Coinbase",
    children: [_jsx("circle", {
      cx: "12",
      cy: "12",
      r: "12",
      fill: "#0052FF"
    }), _jsx("path", {
      d: "M12 4.5a7.5 7.5 0 1 0 0 15 7.5 7.5 0 0 0 0-15zm-1.8 4.8h3.6c.33 0 .6.27.6.6v4.2c0 .33-.27.6-.6.6h-3.6a.6.6 0 0 1-.6-.6V9.9c0-.33.27-.6.6-.6z",
      fill: "white"
    })]
  });
}
export function CheckoutPaymentPanel(t0) {
  const $ = _c(43);
  const {
    recipientAddress,
    destinationTokenAddress,
    destinationTokenChainId,
    totalAmount,
    buttonText,
    themeColor,
    returnUrl,
    returnLabel,
    onSuccess,
    onError,
    callbackMetadata,
    classes,
    defaultPaymentMethod,
    senderAddress,
    showPoints,
    showOrderId,
    isFormValid: t1,
    feeOnTop,
    kycEnabled: t2,
    showFiatOption: t3
  } = t0;
  const isFormValid = t1 === undefined ? true : t1;
  const kycEnabled = t2 === undefined ? false : t2;
  const showFiatOption = t3 === undefined ? true : t3;
  let t4;
  if ($[0] !== defaultPaymentMethod || $[1] !== showFiatOption) {
    t4 = () => {
      if (!showFiatOption && (defaultPaymentMethod === "card" || defaultPaymentMethod === "coinbase")) {
        return "crypto";
      }
      return defaultPaymentMethod ?? null;
    };
    $[0] = defaultPaymentMethod;
    $[1] = showFiatOption;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const [paymentMethod, setPaymentMethod] = useState(t4);
  let t5;
  let t6;
  if ($[3] !== paymentMethod || $[4] !== showFiatOption) {
    t5 = () => {
      if (!showFiatOption && (paymentMethod === "card" || paymentMethod === "coinbase")) {
        setPaymentMethod("crypto");
      }
    };
    t6 = [showFiatOption, paymentMethod];
    $[3] = paymentMethod;
    $[4] = showFiatOption;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  const [activeOrderId, setActiveOrderId] = useState(_temp);
  let t7;
  let t8;
  if ($[7] !== activeOrderId) {
    t7 = () => {
      if (activeOrderId) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, activeOrderId);
      } else {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
    };
    t8 = [activeOrderId];
    $[7] = activeOrderId;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = orderId => {
      setActiveOrderId(orderId);
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const handleOrderCreated = t9;
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setActiveOrderId(null);
    };
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const handleRetry = t10;
  if (activeOrderId) {
    let t11;
    if ($[12] !== activeOrderId || $[13] !== classes || $[14] !== onError || $[15] !== onSuccess || $[16] !== returnLabel || $[17] !== returnUrl || $[18] !== showOrderId || $[19] !== showPoints || $[20] !== themeColor) {
      t11 = _jsx(CheckoutOrderStatus, {
        orderId: activeOrderId,
        themeColor,
        returnUrl,
        returnLabel,
        onSuccess,
        onError,
        onRetry: handleRetry,
        showPoints,
        showOrderId,
        classes
      });
      $[12] = activeOrderId;
      $[13] = classes;
      $[14] = onError;
      $[15] = onSuccess;
      $[16] = returnLabel;
      $[17] = returnUrl;
      $[18] = showOrderId;
      $[19] = showPoints;
      $[20] = themeColor;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    return t11;
  }
  let t11;
  if ($[22] !== classes?.paymentMethodButton) {
    t11 = active => cn("anyspend-payment-method-btn flex w-full items-center gap-3 px-4 py-4 text-left transition-colors", !active && "hover:bg-gray-50 dark:hover:bg-neutral-800", classes?.paymentMethodButton);
    $[22] = classes?.paymentMethodButton;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const accordionButtonClass = t11;
  let t12;
  if ($[24] !== accordionButtonClass || $[25] !== buttonText || $[26] !== callbackMetadata || $[27] !== classes || $[28] !== destinationTokenAddress || $[29] !== destinationTokenChainId || $[30] !== feeOnTop || $[31] !== isFormValid || $[32] !== kycEnabled || $[33] !== onError || $[34] !== paymentMethod || $[35] !== recipientAddress || $[36] !== senderAddress || $[37] !== showFiatOption || $[38] !== themeColor || $[39] !== totalAmount) {
    const expandedPanelClass = cn("anyspend-payment-method-panel border-t border-gray-100 px-4 py-4 dark:border-neutral-800");
    let t13;
    if ($[41] !== paymentMethod) {
      t13 = () => setPaymentMethod(paymentMethod === "crypto" ? null : "crypto");
      $[41] = paymentMethod;
      $[42] = t13;
    } else {
      t13 = $[42];
    }
    t12 = _jsxs("div", {
      className: cn("anyspend-payment-panel flex flex-col gap-5", classes?.paymentPanel),
      children: [_jsx("h2", {
        className: cn("anyspend-payment-title text-lg font-semibold text-gray-900 dark:text-gray-100", classes?.paymentTitle),
        children: "Payment"
      }), !isFormValid && _jsx("p", {
        className: "text-sm text-amber-600 dark:text-amber-400",
        children: "Please complete the required fields above before proceeding to payment."
      }), _jsxs("div", {
        className: cn("anyspend-payment-methods divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200 dark:divide-neutral-700 dark:border-neutral-700", !isFormValid && "pointer-events-none opacity-50", classes?.paymentMethodSelector),
        children: [_jsxs("div", {
          className: "anyspend-method-crypto",
          children: [_jsxs("button", {
            onClick: t13,
            className: accordionButtonClass(paymentMethod === "crypto"),
            children: [_jsx(RadioCircle, {
              selected: paymentMethod === "crypto",
              themeColor
            }), _jsx(Wallet, {
              className: "h-5 w-5 text-gray-700 dark:text-gray-300"
            }), _jsx("span", {
              className: "text-sm font-medium text-gray-900 dark:text-gray-100",
              children: "Pay with crypto"
            })]
          }), _jsx(AnimatePresence, {
            initial: false,
            children: paymentMethod === "crypto" && _jsx(motion.div, {
              initial: {
                height: 0,
                opacity: 0
              },
              animate: {
                height: "auto",
                opacity: 1
              },
              exit: {
                height: 0,
                opacity: 0
              },
              transition: {
                duration: 0.2,
                ease: "easeOut"
              },
              style: {
                overflow: "hidden"
              },
              children: _jsx("div", {
                className: expandedPanelClass,
                children: _jsx(CryptoPayPanel, {
                  recipientAddress,
                  destinationTokenAddress,
                  destinationTokenChainId,
                  totalAmount,
                  buttonText,
                  themeColor,
                  onOrderCreated: handleOrderCreated,
                  onError,
                  callbackMetadata,
                  classes,
                  senderAddress
                })
              })
            }, "crypto-panel")
          })]
        }), showFiatOption && _jsxs(_Fragment, {
          children: [_jsxs("div", {
            className: "anyspend-method-card",
            children: [_jsxs("button", {
              onClick: () => setPaymentMethod(paymentMethod === "card" ? null : "card"),
              className: accordionButtonClass(paymentMethod === "card"),
              children: [_jsx(RadioCircle, {
                selected: paymentMethod === "card",
                themeColor
              }), _jsx(CreditCard, {
                className: "h-5 w-5 text-gray-700 dark:text-gray-300"
              }), _jsx("span", {
                className: "text-sm font-medium text-gray-900 dark:text-gray-100",
                children: "Credit or debit card"
              }), _jsxs("div", {
                className: "ml-auto flex items-center gap-1",
                children: [_jsx(VisaLogo, {}), _jsx(MastercardLogo, {}), _jsx(AmexLogo, {})]
              })]
            }), _jsx(AnimatePresence, {
              initial: false,
              children: paymentMethod === "card" && _jsx(motion.div, {
                initial: {
                  height: 0,
                  opacity: 0
                },
                animate: {
                  height: "auto",
                  opacity: 1
                },
                exit: {
                  height: 0,
                  opacity: 0
                },
                transition: {
                  duration: 0.2,
                  ease: "easeOut"
                },
                style: {
                  overflow: "hidden"
                },
                children: _jsx("div", {
                  className: expandedPanelClass,
                  children: _jsx(FiatCheckoutPanel, {
                    recipientAddress,
                    destinationTokenAddress,
                    destinationTokenChainId,
                    totalAmount,
                    themeColor,
                    onOrderCreated: handleOrderCreated,
                    onError,
                    callbackMetadata,
                    classes,
                    feeOnTop,
                    kycEnabled
                  })
                })
              }, "card-panel")
            })]
          }), _jsxs("div", {
            className: "anyspend-method-coinbase",
            children: [_jsxs("button", {
              onClick: () => setPaymentMethod(paymentMethod === "coinbase" ? null : "coinbase"),
              className: accordionButtonClass(paymentMethod === "coinbase"),
              children: [_jsx(RadioCircle, {
                selected: paymentMethod === "coinbase",
                themeColor
              }), _jsx(CoinbaseLogo, {}), _jsx("span", {
                className: "text-sm font-medium text-gray-900 dark:text-gray-100",
                children: "Coinbase Pay"
              })]
            }), _jsx(AnimatePresence, {
              initial: false,
              children: paymentMethod === "coinbase" && _jsx(motion.div, {
                initial: {
                  height: 0,
                  opacity: 0
                },
                animate: {
                  height: "auto",
                  opacity: 1
                },
                exit: {
                  height: 0,
                  opacity: 0
                },
                transition: {
                  duration: 0.2,
                  ease: "easeOut"
                },
                style: {
                  overflow: "hidden"
                },
                children: _jsx("div", {
                  className: expandedPanelClass,
                  children: _jsx(CoinbaseCheckoutPanel, {
                    recipientAddress,
                    destinationTokenAddress,
                    destinationTokenChainId,
                    totalAmount,
                    themeColor,
                    onOrderCreated: handleOrderCreated,
                    onError,
                    callbackMetadata,
                    classes
                  })
                })
              }, "coinbase-panel")
            })]
          })]
        })]
      })]
    });
    $[24] = accordionButtonClass;
    $[25] = buttonText;
    $[26] = callbackMetadata;
    $[27] = classes;
    $[28] = destinationTokenAddress;
    $[29] = destinationTokenChainId;
    $[30] = feeOnTop;
    $[31] = isFormValid;
    $[32] = kycEnabled;
    $[33] = onError;
    $[34] = paymentMethod;
    $[35] = recipientAddress;
    $[36] = senderAddress;
    $[37] = showFiatOption;
    $[38] = themeColor;
    $[39] = totalAmount;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  return t12;
}
function _temp() {
  if (typeof window !== "undefined") {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) || null;
  }
  return null;
}